import { useState } from "react";
import { timelineCases, type TimelineCase, type TimelineNode } from "@/mocks/timeline";

const nodeStyle: Record<TimelineNode["tone"], string> = {
  key: "border-accent-400 text-accent-300",
  turning: "border-primary-400 text-primary-300",
  info: "border-secondary-400 text-secondary-300",
};

const statusTone: Record<string, string> = {
  accent: "bg-accent-500/15 text-accent-300 border-accent-500/30",
  primary: "bg-primary-500/15 text-primary-300 border-primary-500/30",
  secondary: "bg-secondary-500/20 text-secondary-200 border-secondary-500/30",
};

function MiniTimeline({ nodes }: { nodes: TimelineNode[] }) {
  return (
    <ol className="mt-4 space-y-0 border-l border-foreground-800 pl-0">
      {nodes.map((n, i) => (
        <li key={i} className="relative pb-4 pl-5 last:pb-0">
          <span
            className={`absolute -left-[5px] top-1 flex h-2.5 w-2.5 items-center justify-center rounded-full border-2 bg-foreground-950 ${nodeStyle[n.tone].split(" ")[0]}`}
          />
          <p className={`flex items-center gap-2 text-[11px] font-medium ${nodeStyle[n.tone].split(" ")[1]}`}>
            {n.time}
          </p>
          <p className="mt-0.5 text-sm font-semibold text-background-50">{n.label}</p>
          <p className="mt-0.5 text-xs leading-relaxed text-foreground-300">{n.note}</p>
        </li>
      ))}
    </ol>
  );
}

export default function TimelineSection() {
  const [active, setActive] = useState<string | "all">("all");
  const shown: TimelineCase[] =
    active === "all" ? timelineCases : timelineCases.filter((c) => c.id === active);

  return (
    <section id="timeline" className="scroll-mt-28 bg-foreground-950 py-10 md:py-14">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="mb-2 flex items-center gap-2">
          <span className="h-5 w-1 bg-accent-500" />
          <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">
            追更时间线
          </h2>
        </div>
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <p className="text-xs leading-relaxed text-foreground-300 md:text-sm">
            把实锤、回应与后续进展按时间节点对齐。同一条瓜，节点铺开才看得清来龙去脉。
          </p>
          <div className="flex items-center gap-1 rounded-full bg-foreground-900 p-1">
            {[{ id: "all", label: "全部在追" }, ...timelineCases.map((c) => ({ id: c.id, label: c.title.slice(0, 5) }))].map(
              (t) => (
                <button
                  key={t.id}
                  onClick={() => setActive(t.id)}
                  className={`rounded-full px-3 py-1 text-xs whitespace-nowrap transition-colors ${
                    active === t.id
                      ? "bg-accent-500 font-semibold text-background-50"
                      : "text-foreground-300 hover:bg-foreground-800"
                  }`}
                >
                  {t.label}
                </button>
              )
            )}
          </div>
        </div>

        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {shown.map((c) => (
            <article key={c.id} className="flex flex-col border border-foreground-800 bg-foreground-900 p-4">
              <div className="flex items-center justify-between gap-2">
                <span
                  className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium ${statusTone[c.statusTone]}`}
                >
                  <i className="ri-radar-line" /> {c.status}
                </span>
                <span className="text-[11px] text-foreground-400">{c.person}</span>
              </div>
              <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-background-50">
                {c.title}
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-foreground-300">{c.summary}</p>
              <div className="relative mt-3 aspect-[16/9] overflow-hidden">
                <img src={c.cover} alt={c.title} className="h-full w-full object-cover object-top" />
              </div>
              <MiniTimeline nodes={c.nodes} />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}